import { useEffect, useState, useMemo } from 'react';
import { T } from '../theme';
import { ContentHeader } from './fmxkit';
import { getRecentPushes, getAllExtractionRuns } from '../db';

// Activity — cross-project feed (FMX restyle, step 8).
// Merges three real event sources, newest first, grouped Today / Yesterday /
// Earlier: FMX pushes (project_pushes), completed imports (project_imports,
// passed in from ProjectScreen), and Claude invocations (extraction_runs).

const FEED_CAP = 40;

const STAGE_LABELS = {
  extraction: 'PDF extraction',
  field_mapping: 'Auto-mapping',
  bulk_edit: 'Bulk edit',
  transform: 'Transform',
};

function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}
function fmtDate(iso) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function ActivityScreen({ importRows, projectNameById }) {
  const [pushes, setPushes] = useState([]);
  const [runs, setRuns] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getRecentPushes({ sinceDays: 90 }).then(p => { if (!cancelled) setPushes(p || []); });
    getAllExtractionRuns({ limit: 100 }).then(r => { if (!cancelled) setRuns(r || []); });
    return () => { cancelled = true; };
  }, []);

  const groups = useMemo(() => {
    const items = [];
    const projName = (id) => projectNameById[id] || 'Unknown project';

    for (const p of pushes) {
      if (!p.pushed_at) continue;
      const ok = p.succeeded ?? p.row_count ?? 0;
      const failed = p.failed || 0;
      items.push({
        when: p.pushed_at,
        icon: failed > 0 ? 'rotate' : 'cloud-arrow-up',
        color: failed > 0 ? T.orange500 : T.greenText,
        bg: failed > 0 ? T.tint.warning : T.tint.success,
        text: (
          <span>
            Pushed <b>{ok.toLocaleString()} {p.schema_type} row{ok === 1 ? '' : 's'}</b>
            {p.fmx_site_url ? <> to {p.fmx_site_url}</> : null}
            {failed > 0 && <span style={{ color: T.red500 }}> · {failed} failed</span>}
          </span>
        ),
        meta: projName(p.project_id),
      });
    }

    for (const r of importRows) {
      const when = r.completed_at || r.updated_at;
      if (!when) continue;
      const rows = (r.row_count_stored ?? r.row_count) || 0;
      items.push({
        when,
        icon: 'file-import',
        color: T.brand,
        bg: T.tint.info,
        text: <span>Completed the <b>{r.schema_type}</b> import — {rows.toLocaleString()} rows mapped &amp; validated</span>,
        meta: projName(r.project_id),
      });
    }

    for (const r of runs) {
      if (!r.created_at || r.dry_run) continue;
      const stage = STAGE_LABELS[r.stage] || r.stage;
      const isError = r.status === 'error';
      items.push({
        when: r.created_at,
        icon: isError ? 'rotate' : 'wand-magic-sparkles',
        color: isError ? T.orange500 : T.brand,
        bg: isError ? T.tint.warning : T.tint.info,
        text: isError
          ? <span><b>{stage}</b> for {r.migration_type} hit an error{r.error ? <> — <i>{String(r.error).slice(0, 90)}</i></> : null}</span>
          : <span>Claude ran <b>{stage}</b> for {r.migration_type}{r.source_filename ? <> on <i>{r.source_filename}</i></> : null}</span>,
        meta: [
          r.project_id ? projName(r.project_id) : null,
          r.estimated_cost_usd != null ? `$${Number(r.estimated_cost_usd).toFixed(3)}` : null,
        ].filter(Boolean).join(' · '),
      });
    }

    items.sort((a, b) => new Date(b.when) - new Date(a.when));
    const capped = items.slice(0, FEED_CAP);

    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    const out = [
      { group: 'Today', items: [] },
      { group: 'Yesterday', items: [] },
      { group: 'Earlier', items: [] },
    ];
    for (const it of capped) {
      const d = new Date(it.when).toDateString();
      const bucket = d === today ? out[0] : d === yesterday ? out[1] : out[2];
      const timeLabel = d === today ? fmtTime(it.when) : d === yesterday ? fmtTime(it.when) : fmtDate(it.when);
      bucket.items.push({ ...it, meta: [timeLabel, it.meta].filter(Boolean).join(' · ') });
    }
    return out.filter(g => g.items.length > 0);
  }, [pushes, runs, importRows, projectNameById]);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <ContentHeader crumbs={['Activity']} title="Activity" subtitle="Everything your team and Claude did, newest first" />
      <div style={{ flex: 1, overflow: 'auto', background: T.bg, padding: 20 }}>
        <div style={{ maxWidth: 760 }}>
          {groups.length === 0 && (
            <div style={{ background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusCard, padding: '24px', font: `400 14px ${T.fontSans}`, color: T.inkMuted }}>
              No activity yet — pushes, imports, and Claude runs will show up here.
            </div>
          )}
          {groups.map(g => (
            <div key={g.group} style={{ marginBottom: 18 }}>
              <div style={{ font: `700 11px/16px ${T.fontSans}`, textTransform: 'uppercase', letterSpacing: '0.07em', color: T.inkSoft, margin: '0 0 8px 2px' }}>{g.group}</div>
              <div style={{ background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusCard, overflow: 'hidden' }}>
                {g.items.map((a, i) => (
                  <div key={i} style={{ display: 'flex', gap: 12, padding: '12px 16px', borderBottom: i < g.items.length - 1 ? `1px solid ${T.borderSubtle}` : 'none' }}>
                    <span style={{ width: 30, height: 30, borderRadius: T.radiusPill, background: a.bg, color: a.color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className={`fa-solid fa-${a.icon}`} style={{ fontSize: 12 }} />
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ font: `400 14px/20px ${T.fontSans}`, color: T.ink }}>{a.text}</div>
                      <div style={{ font: `400 12px/16px ${T.fontSans}`, color: T.inkSoft, marginTop: 2 }}>{a.meta}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
